// Copyright 2017 Sorama B.V.

import {
    autoinject,
    customElement,
    observable,
    BindingEngine,
    Disposable,
    bindable,
    bindingMode,
} from "aurelia-framework";
import { ProjectDTO } from "datatypes/SoramaWebApi";
import { StatusItem } from "datatypes/Status";
import { IPagedRequest } from "libraries/WebRequestHandler/IWebRequestHandler";
import { IProjectManagementService } from "services/Project/IProjectManagementService";

@customElement("projects-dropdown")
@autoinject
export class ProjectsDropdown {
    //#region Private Properties
    @observable
    private selectedProject: ProjectDTO;
    private subscription: Disposable;

    @observable
    private filterText: string;

    protected pagedRequest: IPagedRequest<ProjectDTO>;
    //#endregeion

    @bindable({ defaultBindingMode: bindingMode.oneWay, defaultValue: true }) canCreate: boolean;

    //#region Constructor and Aurelia Callbacks
    constructor(
        private readonly projectManagementService: IProjectManagementService,
        private readonly bindingEngine: BindingEngine
    ) {}

    public bind(): void {
        this.selectedProject = this.projectManagementService.currentProject.status.available
            ? this.projectManagementService.currentProject.item
            : undefined;
        this.subscription = this.bindingEngine
            .propertyObserver(this.projectManagementService, "currentProject")
            .subscribe(this.currentProjectChanged);

        // Query the projects list
        this.filterTextChanged(this.filterText);
    }

    public unbind(): void {
        this.subscription.dispose();
        this.subscription = null;
    }

    //#endregion

    //#region Property Changed Methods

    public selectedProjectChanged(newValue: ProjectDTO, oldValue: ProjectDTO): void {
        if (!newValue) return;

        try {
            if (
                !this.projectManagementService.currentProject.status.available ||
                !this.projectManagementService.currentProject.item ||
                newValue.id != this.projectManagementService.currentProject.item.id
            ) {
                this.projectManagementService.setCurrentProject(newValue.id);
            }
        } catch (e) {
            // If, for some reason, we can't select the new project, revert to the old project.
            this.selectedProject = oldValue;
            console.error(e);
        }
    }

    public filterTextChanged(newFilterText: string): void {
        // Create a new paged request with filtering
        this.pagedRequest = this.projectManagementService.getProjects(
            newFilterText
                ? "$filter=contains(Name,'" + newFilterText + "') or contains(Description, '" + newFilterText + "')"
                : undefined
        );
    }

    public currentProjectChanged = (newValue: StatusItem<ProjectDTO>, oldValue: StatusItem<ProjectDTO>): void => {
        if (!newValue.status.available) return;

        if (!newValue.item || !this.selectedProject || this.selectedProject.id !== newValue.item.id) {
            this.selectedProject = newValue.item;
        }
    };

    //#endregion

    //#region User Input
    public createProject(name: string, description?: string): void {
        this.projectManagementService.createProject(name, description).then((project) => {
            // Set the new project as the current project.
            // The selection will automatically change due to the currentProjectChanged callback
            this.projectManagementService.setCurrentProject(project.id);
            this.filterTextChanged(this.filterText); // Update paged request
        });
    }
    //#endregion
}
